import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Header from "../components/Header";
import useAdminOnly from "../hooks/useAdminOnly";

export default function CareersReview() {
  const { allowed } = useAdminOnly();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [search, setSearch] = useState("");
  const [archiving, setArchiving] = useState(null);

  useEffect(() => {
    if (allowed) loadApplications();
  }, [showArchive, allowed]);

  async function loadApplications() {
    setLoading(true);

    let query = supabase
      .from("career_applications")
      .select(`
        id,
        status,
        archived,
        applicant_data,
        created_at,
        discord_username,
        careers ( id, title, department )
      `)
      .order("created_at", { ascending: false });

    if (showArchive) {
      query = query.eq("archived", true);
    } else {
      query = query.or("archived.is.null,archived.eq.false");
    }

    const { data, error } = await query;
    if (!error) setApplications(data || []);
    setLoading(false);
  }

  async function toggleArchive(e, app) {
    e.preventDefault();
    e.stopPropagation();
    setArchiving(app.id);

    const newVal = !app.archived;
    const { error } = await supabase
      .from("career_applications")
      .update({ archived: newVal })
      .eq("id", app.id);

    if (!error) {
      setApplications(prev => prev.filter(a => a.id !== app.id));
    }

    setArchiving(null);
  }

  function getApplicantName(data) {
    return data?.full_name || data?.name || data?.roblox_user || data?.email || "Unknown Applicant";
  }

  const filtered = search.trim()
    ? applications.filter(app => {
        const name = getApplicantName(app.applicant_data).toLowerCase();
        const role = (app.careers?.title || "").toLowerCase();
        const dept = (app.careers?.department || "").toLowerCase();
        const discord = (app.discord_username || "").toLowerCase();
        const q = search.toLowerCase();
        return name.includes(q) || role.includes(q) || dept.includes(q) || discord.includes(q);
      })
    : applications;

  if (allowed === null) return null; // still checking
  if (!allowed) return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <Header />
      <p className="text-white/40 text-sm mt-32">You don't have permission to view this page.</p>
    </main>
  );

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-20 md:px-[7%]">
        <p className="font-black uppercase text-[#ffb56f]">Admin</p>
        <h1 className="outfall-heading text-[clamp(58px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Applications
        </h1>

        {/* Controls */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search applicant, role, department..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="rounded-xl border border-[#ffb56f]/30 bg-black/50 px-4 py-2.5 font-bold text-white outline-none placeholder:text-white/30 focus:border-[#ffd978] w-72"
          />
          <button
            onClick={() => { setShowArchive(v => !v); setSearch(""); }}
            className={`rounded-xl border px-5 py-2.5 font-black uppercase text-sm transition ${
              showArchive
                ? "bg-[#ffd978] text-black border-[#ffd978]"
                : "border-[#ffd978]/30 text-white/50 hover:text-white"
            }`}
          >
            {showArchive ? "Viewing Archive" : "View Archive"}
          </button>
        </div>

        <div className="mt-4 overflow-hidden rounded-[24px] border-4 border-[#ffd978] bg-black/60 shadow-[0_0_35px_rgba(255,143,107,0.35)]">
          {loading ? (
            <p className="p-6 text-xl font-black">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-xl font-black">
              {showArchive ? "No archived applications." : "No applications found."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] border-collapse">
                <thead className="bg-[#ffd978] text-black">
                  <tr>
                    <th className="px-5 py-4 text-left font-black uppercase">Applicant</th>
                    <th className="px-5 py-4 text-left font-black uppercase">Role</th>
                    <th className="px-5 py-4 text-left font-black uppercase">Department</th>
                    <th className="px-5 py-4 text-left font-black uppercase">Status</th>
                    <th className="px-5 py-4 text-left font-black uppercase">Submitted</th>
                    <th className="px-5 py-4 text-left font-black uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((app) => (
                    <tr key={app.id} className="border-t border-[#ffb56f]/20 hover:bg-white/5">
                      <td className="px-5 py-4 font-black text-white">
                        {getApplicantName(app.applicant_data)}
                        {app.discord_username && (
                          <span className="ml-2 text-xs font-bold text-white/30">{app.discord_username}</span>
                        )}
                      </td>
                      <td className="px-5 py-4 font-bold text-white">{app.careers?.title || "Unknown Role"}</td>
                      <td className="px-5 py-4 font-bold text-white">{app.careers?.department || "N/A"}</td>
                      <td className="px-5 py-4">
                        <span className="rounded-full bg-[#ffb56f] px-3 py-1 text-sm font-black uppercase text-black">
                          {app.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-bold text-white/60">
                        {new Date(app.created_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Link to={`/careers-review/${app.id}`} className="outfall-button">
                            View
                          </Link>
                          <button
                            onClick={e => toggleArchive(e, app)}
                            disabled={archiving === app.id}
                            title={app.archived ? "Unarchive" : "Archive"}
                            className="rounded-lg border border-white/20 px-3 py-2 text-xs font-black uppercase text-white/40 transition hover:border-white/40 hover:text-white/70 disabled:opacity-30"
                          >
                            {archiving === app.id ? "..." : app.archived ? "Unarchive" : "Archive"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
